"use client";

import { ChevronLeftIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from "react";

import { trackOnboardingStepViewed } from "@/lib/analytics/client-events";
import type { Profession } from "@/lib/onboarding/constants";
import { readDraft, writeDraft } from "@/lib/onboarding/draft";
import {
  applicableAnswers,
  isChromeless,
  isPostSubmitStep,
  nextStep,
  prevStep,
  progressFor,
  resumeStep,
  type FlowContext,
  type FlowState,
  type StepId,
} from "@/lib/onboarding/flow";
import { cn } from "@/lib/utils";

import { STEP_COMPONENTS } from "./steps";
import { OnboardingFrame, ProgressBar } from "./ui";
import type { AnswersPatch, OnboardingEnv, PreSubmitStepId } from "./types";

type WizardState = { flow: FlowState; step: PreSubmitStepId; direction: 1 | -1 };

const noopSubscribe = () => () => {};

/** The pre-submit half of the guided onboarding. Owns navigation, the draft,
 *  and the funnel event; steps own only their screen.
 *
 *  Renders nothing on the server: the starting step depends on the draft in
 *  localStorage, which only the browser has. Rendering a guess and then
 *  correcting it would flash the wrong screen (and mismatch on hydration). */
export function OnboardingWizard({ env }: { env: OnboardingEnv }) {
  const isClient = useSyncExternalStore(
    noopSubscribe,
    () => true,
    () => false,
  );
  if (!isClient) return <div className="flex-1" aria-busy="true" />;
  // Keyed on the session: if it appears or lapses (the submit sends a
  // signed-out user back here), the starting step is re-derived from scratch.
  return <WizardInner key={String(env.authed)} env={env} />;
}

function toPreSubmit(step: StepId | null): PreSubmitStepId {
  // A post-submit step can't be reached from here (the about submit leaves
  // this route); treat one as "about" so a malformed draft can't strand us.
  return step === null || isPostSubmitStep(step) ? "about" : step;
}

/** Apply a step's patch, then drop answers that no longer apply (flow.ts
 *  `applicableAnswers`), so a role or track switch can't leave stale answers
 *  behind for the readback screens or the submit. */
function merge(flow: FlowState, patch?: AnswersPatch, profession?: Profession): FlowState {
  if (!patch && profession === undefined) return flow;
  const next: FlowState = {
    answers: { ...flow.answers, ...patch },
    profession: profession ?? flow.profession,
  };
  return { ...next, answers: applicableAnswers(next) };
}

function WizardInner({ env }: { env: OnboardingEnv }) {
  const ctx: FlowContext = useMemo(() => ({ authed: env.authed }), [env.authed]);

  const [w, setW] = useState<WizardState>(() => {
    const draft = readDraft();
    const loaded: FlowState = draft
      ? { answers: draft.answers, profession: draft.profession }
      : { answers: {} };
    const flow: FlowState = { ...loaded, answers: applicableAnswers(loaded) };
    return { flow, step: toPreSubmit(resumeStep(draft?.step, flow, ctx)), direction: 1 };
  });

  // Persist on every change. writeDraft refuses anything outside the
  // non-personal pick, so this can't leak a typed name into storage.
  useEffect(() => {
    writeDraft({ step: w.step, profession: w.flow.profession, answers: w.flow.answers });
  }, [w]);

  // A sign-in error arrives as ?oauth_error and is shown once on the account
  // step; drop it from the URL so a later visit doesn't show it again.
  useEffect(() => {
    const url = new URL(window.location.href);
    if (!url.searchParams.has("oauth_error")) return;
    url.searchParams.delete("oauth_error");
    url.searchParams.delete("oauth_error_code");
    window.history.replaceState(window.history.state, "", url);
  }, []);

  // The funnel event, once per step render. The ONLY call site.
  useEffect(() => {
    trackOnboardingStepViewed({
      step: w.step,
      audience: w.flow.answers.audience ?? null,
      profession: w.flow.profession ?? null,
      track: w.flow.answers.track ?? null,
      authed: env.authed,
    });
    // Deliberately keyed on the step only: an answer changing within a step
    // is not a new view.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [w.step]);

  const update = useCallback((patch: AnswersPatch, profession?: Profession) => {
    setW((prev) => ({ ...prev, flow: merge(prev.flow, patch, profession) }));
  }, []);

  const advance = useCallback(
    (patch?: AnswersPatch, profession?: Profession) => {
      setW((prev) => {
        const flow = merge(prev.flow, patch, profession);
        const next = nextStep(prev.step, flow, ctx);
        return next ? { flow, step: toPreSubmit(next), direction: 1 } : { ...prev, flow };
      });
    },
    [ctx],
  );

  const back = useCallback(() => {
    setW((prev) => {
      const target = prevStep(prev.step, prev.flow, ctx);
      return target ? { ...prev, step: toPreSubmit(target), direction: -1 } : prev;
    });
  }, [ctx]);

  const Step = STEP_COMPONENTS[w.step];
  const chromeless = isChromeless(w.step);
  const canGoBack = prevStep(w.step, w.flow, ctx) !== null;
  const progress = progressFor(w.step, w.flow, ctx);

  return (
    <OnboardingFrame>
      {chromeless ? (
        <div className="h-11" />
      ) : (
        <div className="flex h-11 items-center gap-3.5">
          {canGoBack ? (
            <button
              type="button"
              onClick={back}
              aria-label="Back"
              className="flex size-11 shrink-0 items-center justify-center rounded-xl border border-border bg-card text-muted-foreground transition-colors outline-none hover:bg-muted focus-visible:ring-3 focus-visible:ring-ring/50"
            >
              <ChevronLeftIcon className="size-5" />
            </button>
          ) : (
            <div className="size-11 shrink-0" />
          )}
          <ProgressBar index={progress.index} total={progress.total} />
          <div className="size-11 shrink-0" />
        </div>
      )}

      <main
        key={w.step}
        className={cn(
          "flex flex-1 flex-col pt-7 md:pt-10 motion-safe:animate-in motion-safe:fade-in motion-safe:duration-300",
          w.direction === 1
            ? "motion-safe:slide-in-from-bottom-1"
            : "motion-safe:slide-in-from-top-1",
        )}
      >
        <Step state={w.flow} env={env} update={update} advance={advance} />
      </main>
    </OnboardingFrame>
  );
}
